import { Box, Tab, Tabs } from "@mui/material";
import ToolBar from "../../../components/ToolBar";
import { handleICVActivitiesPaginationDataSlice, handleUpdatePageSlice } from "../../../redux/slices/globalSlice";
import { handleCHMGisChartDataSlice, handleCHMInfoByIdDataSlice } from "../../../redux/slices/chmActivitySlice";
import { handleGetIcvDataSlice, handleICVGetInfoDataSlice } from "../../../redux/slices/icvActivitySlice";
import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import ICVActivities from "./ICVActivities";
import ICVMasters from "./ICVMasters";
import ICVQCActivites from "../icvQCActivities/ICVQCActivies";
import { LoggedUserType } from "../../../models/GlobalModels";
import { loginSelector } from "../../../redux/slices/loginSlice";
import NoPermission from "../../../NoAccess/NoPermission";
import { refreshAuthToken } from "../../../utils/AuthUtils";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <div className="pt-3 tab-container">{children}</div>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const ICV = () => {
    const allTabOptions = [
        { label: 'ICV Masters', tabId: 0, permission: 'ICV-Masters-Tab', component: ICVMasters },
        { label: 'ICV Activities', tabId: 1, permission: 'ICV-Activities-Tab', component: ICVQCActivites },
        { label: 'Completed ICV', tabId: 2, permission: 'Completed-ICV-Tab', component: ICVActivities }
    ];

    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const { storeICVActivitiesPaginationData } = useSelector((state: any) => state.global);
    const user: LoggedUserType = useSelector(loginSelector).user;

    const authorizedTabs = useMemo(() => {
        return allTabOptions
            .filter(tab => user?.permissions?.includes(tab.permission))
            .map((tab, index) => ({
                ...tab,
                displayIndex: index 
            }));
    }, [user?.permissions]);

    const [value, setValue] = useState<number>(() => {
        const savedValue = localStorage.getItem("selectedICVTab");
        if (savedValue !== null) {
            const savedTabId = parseInt(savedValue, 10);
            // Find if the saved tab is still authorized
            const authorizedTab = authorizedTabs.find(tab => tab.tabId === savedTabId);
            if (authorizedTab) {
                return authorizedTab.displayIndex;
            }
        }
        // Default to first authorized tab
        return authorizedTabs.length > 0 ? 0 : 0;
    });

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        // Save the actual tabId, not the display index
        const selectedTab = authorizedTabs[newValue];
        if (selectedTab) {
            localStorage.setItem("selectedICVTab", selectedTab.tabId.toString());
        }
        
        // Conditionally clear ICV data based on tab navigation
        if (selectedTab?.tabId === 1) {
            // ICV Activities tab - clear ICV data
            dispatch(handleGetIcvDataSlice({ data: [], count: 0 }));
        } else if (selectedTab?.tabId === 2) {
            // Completed ICV tab - clear ICV info data
            dispatch(handleICVGetInfoDataSlice({ data: [], count: 0 }));
        }
        
        dispatch(handleUpdatePageSlice(false));
        dispatch(handleCHMInfoByIdDataSlice({}));
        dispatch(handleCHMGisChartDataSlice({}));
        dispatch(
            handleICVActivitiesPaginationDataSlice({
                ...storeICVActivitiesPaginationData,
                offset: 0,
                limit: 250,
                selectedLimit: 250,
            })
        );
    };

    // If no tabs are authorized, show a message
    if (authorizedTabs.length === 0) {
        return (
            <>
                <div style={{position: 'sticky', top: '0', zIndex:'1000'}}>
                    <ToolBar title={'Insured Crop Verification (ICV)'} className={'rounded-toolbar'} />
                </div>
                <div className="pt-3">
                    <NoPermission/>
                </div>
            </>
        );
    }

    return (
        <>
            <div style={{position: 'sticky', top: '0', zIndex:'1000'}}>
                <ToolBar
                    title={'Insured Crop Verification (ICV)'}
                />

                <div className="tab-section">
                    <Tabs
                        TabIndicatorProps={{ style: { background: "rgba(92, 121, 170, 1)" } }}
                        value={value}
                        onChange={handleChange}
                    >
                        {authorizedTabs.map((tab) => (
                            <Tab
                                key={tab.tabId}
                                label={tab.label}
                                style={{ 
                                    color: "rgba(92, 121, 170, 1)", 
                                    fontWeight: "600", 
                                    textTransform: 'none', 
                                    minWidth: `33%` 
                                }}
                                {...a11yProps(tab.displayIndex)}
                            />
                        ))}
                    </Tabs>
                </div>
            </div>

            {/* Render panels with correct display indices */}
            {authorizedTabs.map((tab) => {
                const Component = tab.component;
                return (
                    <CustomTabPanel key={tab.tabId} value={value} index={tab.displayIndex}>
                        <Component />
                    </CustomTabPanel>
                );
            })}
        </>
    );
}

export default ICV;